import { Phone, Clock } from "lucide-react";
import { useCallTracker } from "@/components/app/CallTracker";

/**
 * Big pulse "CALL {name} NOW" button used inside telecaller lead cards.
 *
 * The office / telecalling staff who use this component MUST see the full
 * customer number so they can actually dial. Masking + eye-toggle was
 * getting in the way of the job (see product decision 2026-02) — this
 * component now renders the number in full. PII gating for other roles
 * (bank_staff) still happens server-side via `_pii_masked` on their own
 * dashboard, so there's no data-leak risk.
 *
 * Dialing still only happens through the CALL button so every call is
 * tracked; the visible number is display-only.
 */
export default function TrackedCallButton({ lead }) {
  const { startCall, lastByLead } = useCallTracker();
  if (!lead || !lead.phone) return null;

  const phone = String(lead.phone);
  const last = lastByLead?.[lead.lead_id];
  const ago = (iso) => {
    if (!iso) return "";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  return (
    <div className="mt-2" data-testid={`tracked-call-block-${lead.lead_id}`}>
      {/* Full customer phone — telecaller needs to see it to work the case */}
      <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
        <Phone className="w-3.5 h-3.5 text-emerald-400"/>
        <span className="font-mono-alt select-all tracking-wide"
              data-testid={`phone-full-${lead.lead_id}`}>
          {phone}
        </span>
      </div>

      {/* Big pulse Call button */}
      <button
        type="button"
        onClick={() => startCall(lead)}
        data-testid={`tracked-call-btn-${lead.lead_id}`}
        className="w-full h-14 rounded-full bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] transition text-slate-900 font-bold text-base flex items-center justify-center gap-2 shadow-[0_0_0_4px_rgba(16,185,129,0.15)] animate-pulse">
        <Phone className="w-5 h-5"/>
        CALL {(lead.name || "CUSTOMER").split(" ")[0].toUpperCase()} NOW
      </button>

      {/* Last-call preview */}
      {last && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500" data-testid={`last-call-${lead.lead_id}`}>
          <Clock className="w-3 h-3"/>
          <span>Last call: {ago(last.end_time)} · {last.duration_formatted} ·
            <span className={last.is_fake ? "text-red-400 ml-1" : last.band === "short" ? "text-amber-400 ml-1" : "text-emerald-400 ml-1"}>
              {last.outcome || "—"}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
